import {useEffect, useState} from "react";
import useHubspotEmbed from "@/hooks/forms/use-hubspot-embed";
import {getCookie} from "@/utils/hubspot-utils/hubspot-embed-utils";
import {HubSpotFormInfo} from "@/types/dto/common/hubspot";

const BROCHURE_SUBMITTED_EVENT = "brochure:cookieSet";

interface UseViewBrochureOnlineProps {
  viewBrochureOnlineForm: {
    callToActionText?: string;
    callToActionUrl?: string;
    hubspotFormInfo?: HubSpotFormInfo;
  };
  brochureInfo?: {
    brochureName: string;
    brochureQuantity: number;
  };
  targetId: string;
  instanceId?: string;
}

const useViewBrochureOnline = ({
  viewBrochureOnlineForm,
  targetId,
  instanceId,
  brochureInfo
}: UseViewBrochureOnlineProps) => {
  const [brochureCookieSet, setBrochureCookieSet] = useState<boolean>(false);
  const [viewOnlineFormIsOpen, setViewOnlineFormIsOpen] = useState<boolean>(false);

  useEffect(() => {
    const handleBrochureSubmitted = () => {
      setBrochureCookieSet(true);
    };

    window.addEventListener(BROCHURE_SUBMITTED_EVENT, handleBrochureSubmitted);
    return () => {
      window.removeEventListener(BROCHURE_SUBMITTED_EVENT, handleBrochureSubmitted);
    };
  }, []);

  const handleClick = () => {
    if (brochureCookieSet) return;
    setViewOnlineFormIsOpen((prev) => !prev);
  };

  const handleSubmit = () => {

    const attrs: string[] = [
      `Max-Age=${60 * 60 * 24 * 14}`, // seconds
      "path=/"
    ];

    document.cookie = `brochureSignUpModalShown=true; ${attrs.join("; ")}`;
    setBrochureCookieSet(true);

    window.dispatchEvent(new CustomEvent(BROCHURE_SUBMITTED_EVENT));

    window.open(viewBrochureOnlineForm?.callToActionUrl, "_blank", "noopener,noreferrer")?.focus();
  };

  const hubspotFormInfo = viewBrochureOnlineForm?.hubspotFormInfo;
  const shouldInitHubspot = !!(hubspotFormInfo && hubspotFormInfo.formId && hubspotFormInfo.formName && hubspotFormInfo.portalId);

  const formId = shouldInitHubspot ? hubspotFormInfo.formId : "";

  const {
    dataLayerProps: viewOnlineDataLayerProps,
    isSubmitted: viewOnlineSubmitted,
    isLoading: viewOnlineLoading
  } = useHubspotEmbed({
    formId,
    formName: shouldInitHubspot ? hubspotFormInfo.formName : "",
    portalId: shouldInitHubspot ? hubspotFormInfo.portalId : "",
    brochureInfo,
    targetId,
    onSubmit: handleSubmit,
    instanceId
  });

  useEffect(() => {
    const value = getCookie("brochureSignUpModalShown");
    setBrochureCookieSet(!!value);
  }, []);

  useEffect(() => {
    if (viewOnlineSubmitted) {
      setViewOnlineFormIsOpen(false);
    }
  }, [viewOnlineSubmitted]);

  return {
    viewOnlineFormIsOpen,
    handleClick,
    viewOnlineDataLayerProps,
    viewOnlineLoading,
    viewOnlineSubmitted,
    brochureCookieSet
  };
};

export default useViewBrochureOnline;